import "highcharts/modules/drilldown";
import "highcharts/modules/no-data-to-display";

import { Col } from "antd";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";

interface WalletGraphProps {
  data: any;
}

// Transform API response
const transformWalletData = (api: any) => {
  if (!api?.items?.length) return null;

  const item = api.items[0];
  const monthData = item.GraphData;
  const dayData = item.drilldown;

  return {
    Series: true,
    items: {
      GraphData: [
        {
          Name: "Opening Balance",
          Key: "Opening",
          Data: monthData.map((m: any) => ({
            Name: m.MonthName,
            Y: m.Total_Opening_Balance,
          })),
        },
        {
          Name: "Closing Balance",
          Key: "Closing",
          Data: monthData.map((m: any) => ({
            Name: m.MonthName,
            Y: m.Total_Closing_Balance,
          })),
        },
      ],

      DrilDown: monthData.flatMap((month: any) => {
        const monthDays = dayData.filter((d: any) => d.GMonth === month.GMonth);

        return [
          {
            id: `${month.MonthName}_Opening`,
            name: `${month.MonthName} Opening Balance (Daily)`,
            data: monthDays.map((d: any) => [
              `${d.GDay}`,
              d.Total_Opening_Balance,
            ]),
          },
          {
            id: `${month.MonthName}_Closing`,
            name: `${month.MonthName} Closing Balance (Daily)`,
            data: monthDays.map((d: any) => [
              `${d.GDay}`,
              d.Total_Closing_Balance,
            ]),
          },
        ];
      }),
    },
  };
};

export const WalletGraph = ({ data }: WalletGraphProps) => {
  const formatted = useMemo(() => transformWalletData(data), [data]);

  const options = useMemo(() => {
    if (!formatted || !formatted.Series) return {};

    const series = formatted.items.GraphData.map((s: any) => ({
      name: s.Name,
      data: s.Data.map((item: any) => ({
        name: item.Name,
        y: item.Y,
        drilldown: item.Name + "_" + s.Key,
      })),
      color: s.Key === "Opening" ? "#5bc0de" : "#f0ad4e",
    }));

    const drilldownSeries = formatted.items.DrilDown.map((d: any) => ({
      id: d.id,
      name: d.name,
      data: d.data,
      color: d.id.includes("Opening") ? "#5bc0de" : "#f0ad4e",
    }));

    return {
      chart: {
        type: "column",
        backgroundColor: "transparent",
      },

      title: {
        text: "Opening Balance Vs Closing Balance",
        style: { color: "#000", fontSize: "16px", fontWeight: "bold" },
      },

      xAxis: {
        type: "category",
        labels: { style: { color: "#000", fontSize: "12px" } },
      },

      yAxis: {
        title: {
          text: "Balance Amount",
          style: { color: "#000", fontSize: "14px" },
        },
        labels: { style: { color: "#000", fontSize: "12px" } },
      },

      legend: {
        enabled: true,
        itemStyle: {
          color: "#000",
          fontSize: "10px",
          fontWeight: "bold",
        },
      },

      tooltip: {
        useHTML: true,
        formatter: function () {
          // Detect if the tooltip is for drilldown (daily)
          const isDaily = /^\d+$/.test(this.point.name); // Day number

          if (isDaily) {
            // Extract month & year from chart title context
            const monthName: any = this.series.name.split(" ")[0]; // e.g. "November"
            const months: any = {
              January: "Jan",
              February: "Feb",
              March: "Mar",
              April: "Apr",
              May: "May",
              June: "Jun",
              July: "Jul",
              August: "Aug",
              September: "Sep",
              October: "Oct",
              November: "Nov",
              December: "Dec",
            };

            return `
        <div style="font-size:14px; line-height:18px; font-weight:500;">
          <div>Date: ${this.point.name} ${
              months[monthName]
            } ${new Date().getFullYear()}</div>
          <div>Balance: ${this.point.y.toLocaleString()}</div>
        </div>
      `;
          }

          // Default (Monthly view)
          return `
      <div style="font-size:14px; line-height:18px; font-weight:500;">
        <div>Month: ${this.point.name}</div>
        <div>Balance: ${this.point.y.toLocaleString()}</div>
      </div>
    `;
        },
        style: {
          fontSize: "14px",
          color: "#000",
        },
      } as any,
      plotOptions: {
        series: {
          borderWidth: 0,
          dataLabels: {
            enabled: false,
            color: "#000",
            style: { fontSize: "10px" },
          },
        },
      },

      series,
      drilldown: {
        breadcrumbs: {
          position: { align: "right" },
          style: {
            color: "#000",
            fontSize: "18px",
            fontWeight: "bold",
            textDecoration: "none",
          },
        },
        series: drilldownSeries,
      },

      credits: { enabled: false },
    };
  }, [formatted]);

  return (
    <div style={{ minWidth: "600px" }}>
      <Col span={24}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Col>
    </div>
  );
};
